import { ButtonPrimary, IconButton } from "@nova/component-btn"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import * as Icons from "@nova/component-icons"
import { useRouter } from "pages"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { useTimeAgo } from "@nova/toolkit-pure-hooks"
import { WorkspaceCardViewers } from "features/data-management/workspace/workspace/body/projects-and-folders/grid-view/WorkspaceCardViewers"
import { useLazyLoadedProjectThumbnail } from "tools/hooks/useLazyLoadedProjectThumbnail"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { PortalledContextMenu } from "features/popups/PortalledContextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { beginProjectLocationOverride } from "features/overview/location-override/thunks"
import { useViewport } from "features/map/GlobalViewState"
import { useEffect, useRef } from "react"

export const ProjectPopup = ({ id }: { id: string }) => {
    const router = useRouter("/workspace/:workspaceId")
    const dispatch = useAppDispatch()
    const project = useAppSelector(selectFireById(id))
    const { blobUrl, containerRef, isFetching } = useLazyLoadedProjectThumbnail({
        projectId: id,
    })
    const updatedAgo = useTimeAgo({
        timestamp: project?.updatedAt ? Date.parse(project.updatedAt) : undefined,
    })
    const activeContextMenu = useAppSelector(selectActiveContextMenu("OverviewProject"))
    const isMenuOpen = activeContextMenu?.data.projectId === id
    const isMenuOpenRef = useRef(isMenuOpen)
    useEffect(() => {
        isMenuOpenRef.current = isMenuOpen
    }, [isMenuOpen])
    isMenuOpenRef.current = isMenuOpen
    const viewport = useViewport()

    useEffect(() => {
        if (isMenuOpenRef.current) dispatch(setActiveContextMenu(undefined))
    }, [viewport, dispatch])

    if (!project) return null
    return (
        <div
            className="pointer-events-auto w-[300px] rounded-xl bg-black-800 p-4 flex flex-col gap-4 backdrop-blur-sm cursor-default"
            onMouseDown={(e) => e.stopPropagation()}
        >
            <div className="flex flex-col gap-3">
                <div className="w-full h-[120px] relative">
                    <div ref={containerRef} className="absolute inset-0 rounded-lg overflow-hidden">
                        {blobUrl && (
                            <router.Link
                                to={{
                                    path: "/project/:projectId",
                                    params: { projectId: project.id },
                                }}
                                className="w-full h-full"
                            >
                                <img
                                    className="w-full h-full object-cover object-center"
                                    src={blobUrl}
                                    alt="project"
                                />
                            </router.Link>
                        )}
                        {isFetching && (
                            <div className="w-full shimmer-loading-gradient overflow-hidden rounded-md" />
                        )}
                    </div>
                    <WorkspaceCardViewers
                        viewers={project.viewers}
                        drones={[]}
                        className="absolute -bottom-3 right-4 pointer-events-none"
                    />
                    {project.drones.length > 0 && (
                        <small className="absolute top-2 left-2 px-1 rounded-md bg-red-500 text-white telegraf">
                            Live
                        </small>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex flex-col grow min-w-0">
                        <div className="telegraf text-xl text-white truncate">{project.name}</div>
                        <small className="font-mono text-white-400">
                            Last edited: {updatedAgo}
                        </small>
                    </div>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation()
                            const rect = (e.target as HTMLElement).getBoundingClientRect()
                            dispatch(
                                setActiveContextMenu({
                                    type: "OverviewProject",
                                    data: { projectId: id },
                                    position: {
                                        top: rect.bottom ?? 0,
                                        left: rect.left ?? 0,
                                    },
                                })
                            )
                        }}
                    >
                        <Icons.SmallIcon
                            Icon={Icons.ThreeDotsSettings}
                            className="text-gray-300 hover:text-gray-50"
                        />
                    </IconButton>
                    {isMenuOpen && activeContextMenu && (
                        <PortalledContextMenu
                            position={{
                                x: activeContextMenu.position.left,
                                y: activeContextMenu.position.top,
                            }}
                        >
                            <ContextMenu
                                sections={[
                                    [
                                        {
                                            title: "Update location",
                                            Icon: Icons.AddLocation,
                                            onClick: () => {
                                                dispatch(setActiveContextMenu(undefined))
                                                dispatch(
                                                    beginProjectLocationOverride({ projectId: id })
                                                )
                                            },
                                        },
                                    ],
                                ]}
                                onBlur={() => dispatch(setActiveContextMenu(undefined))}
                            />
                        </PortalledContextMenu>
                    )}
                </div>
            </div>
            <div className="w-full h-px bg-white-200" />
            {project.viewers.length > 0 && (
                <div className="flex flex-col gap-2">
                    <small className="xs text-gray-300">Active</small>
                    <ScrollableArea
                        className="max-h-[112px] shrink min-h-0"
                        onWheel={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col gap-0.5">
                            {project.viewers.map((viewer) => {
                                return (
                                    <div key={viewer.id} className="p-2 flex items-center gap-2">
                                        <div className="w-7 h-7 grow-0 shrink-0 rounded-md overflow-hidden">
                                            <ProfilePhoto
                                                userId={viewer.userId}
                                                fullName={viewer.name}
                                            />
                                        </div>
                                        <div className="text-gray-50 truncate">{viewer.name}</div>
                                    </div>
                                )
                            })}
                        </div>
                    </ScrollableArea>
                </div>
            )}
            <router.Link
                to={{ path: "/project/:projectId", params: { projectId: project.id } }}
                className="w-full"
            >
                <ButtonPrimary
                    title="Go to Project"
                    RightIcon={Icons.ArrowRight}
                    className="w-full"
                />
            </router.Link>
        </div>
    )
}
